/**
 * Editor Configuration Constants Module
 * Default configurations for different editor types
 *
 * @module EditorConstants
 */

/**
 * Menubar configuration for each editor type
 * Controls visibility and available menus
 *
 * @constant
 * @type {{ target: string, source: false }}
 *
 * @example
 * // Target editor shows full menubar
 * EDITOR_MENUBAR.target // 'file edit view insert format tools table help'
 * // Source editor hides menubar
 * EDITOR_MENUBAR.source // false
 */
export const EDITOR_MENUBAR = {
  target: 'file edit view insert format tools table help',
  source: false,
} as const;

/**
 * Plugin configuration for each editor type
 * Defines available functionality
 *
 * @constant
 * @type {{ target: string[], source: string[] }}
 *
 * @example
 * // Target editor has full plugin set
 * EDITOR_PLUGINS.target // ['powerpaste', 'preview', ...]
 * // Source editor has minimal plugins
 * EDITOR_PLUGINS.source // ['table', 'searchreplace']
 */
export const EDITOR_PLUGINS = {
  target: [
    'powerpaste',
    'preview',
    'importcss',
    'searchreplace',
    'autolink',
    'autosave',
    'save',
    'directionality',
    'code',
    'visualblocks',
    'visualchars',
    'fullscreen',
    'image',
    'link',
    'media',
    'template',
    'codesample',
    'table',
    'charmap',
    'pagebreak',
    'nonbreaking',
    'anchor',
    'insertdatetime',
    'advlist',
    'lists',
    'wordcount',
    'charmap',
    'emoticons',
    'tableofcontents',
    'typography',
    'autocorrect',
    'advtable',
    'formatpainter',
    'footnotes',
    'permanentpen',
  ],
  source: [
    'table',
    'searchreplace',
    'charmap',
    'advlist',
    'lists',
    'advtable',
    'fontfamily',
    'fontsize',
    'help',
  ],
} as const;

/**
 * Toolbar configuration for each editor type
 * Defines available toolbar buttons and their order
 *
 * @constant
 * @type {{ target: string[], source: string[] }}
 *
 * @example
 * // Target editor has full toolbar (with undo only, no redo)
 * EDITOR_TOOLBAR.target // ['undo', 'bold italic ...']
 * // Source editor has comprehensive formatting toolbar
 * EDITOR_TOOLBAR.source // ['bold', 'italic', 'underline', 'backcolor', ...]
 */
export const EDITOR_TOOLBAR = {
  target: [
    'undo',
    'bold italic underline strikethrough backcolor tablecellbackgroundcolor',
    'searchreplace',
    'fontfamily fontsize blocks',
    'alignleft aligncenter alignright alignjustify',
    'outdent indent',
    'numlist bullist',
    'forecolor removeformat',
    'table',
    'pagebreak',
    'charmap',
    'fullscreen preview save print',
    'ltr rtl',
    'tableofcontents typography formatpainter footnotes permanentpen',
  ],
  source: [
    'bold',
    'italic',
    'underline',
    'strikethrough',
    'backcolor tablecellbackgroundcolor',
    'searchreplace',
    'fontsize blocks',
    'alignleft aligncenter alignright alignjustify',
    'outdent indent',
    'numlist bullist',
    'forecolor removeformat',
    'table',
    'charmap',
  ],
} as const;

/**
 * Removed menu items configuration for each editor type
 * Defines which menu items should be hidden
 *
 * @constant
 * @type {{ target: string[], source: undefined }}
 *
 * @example
 * // Target editor removes specific items
 * EDITOR_REMOVED_MENUITEMS.target // ['newdocument', 'restoredraft', ...]
 * // Source editor keeps default menu items
 * EDITOR_REMOVED_MENUITEMS.source // undefined
 */
export const EDITOR_REMOVED_MENUITEMS = {
  target: [
    'newdocument',
    'restoredraft',
    'print',
    'code',
    'visualaid',
    'media',
    'template',
    'codesample',
    'emoticons',
    'anchor',
    'insertdatetime',
    'codeformat',
  ],
  source: undefined,
} as const;
